import { useRef } from 'react';
import { getPublicArchiveIndex, searchMember } from '../../api/memberApi';
import { ArchiveIndexListResponse, MemberSearchListResponse } from '../../dto/ResDtoRepository';
import { MemberSearchData } from '../../dto/EntityRepository';
import { useLandingMemberStore } from '../../store/landingMemberStore';
import { useApiWrapper } from './apiWrapper';

export interface UseLandingMemberResult {
  searchList: MemberSearchData[];
  search: (username: string) => Promise<void>;
  clearSearchList: () => void;
  getArchiveIndex: () => Promise<void>;
}

/**
 * 랜딩 전용: `useLandingMemberStore` + 멤버 검색 / Archive Index API.
 * (`useMember`는 계정·비밀번호·핸들만 담당)
 */
export const useLandingMember = (): UseLandingMemberResult => {
  const searchList = useLandingMemberStore((s) => s.searchList);
  const setSearchList = useLandingMemberStore((s) => s.setSearchList);
  const clearSearchListStore = useLandingMemberStore((s) => s.clearSearchList);
  const setArchiveIndexList = useLandingMemberStore((s) => s.setArchiveIndexList);
  const withApiHandler = useApiWrapper();
  const latestSearchId = useRef(0);

  const handleSearchSuccess = (response: MemberSearchListResponse) => {
    const { members } = response.data;
    setSearchList(Array.isArray(members) ? members : []);
  };

  const handleArchiveIndexSuccess = (response: ArchiveIndexListResponse) => {
    const { members } = response.data;
    setArchiveIndexList(Array.isArray(members) ? members : []);
  };

  const search = async (username: string) => {
    const searchId = ++latestSearchId.current;
    const apiFunction = async () => {
      try {
        const response = await searchMember(username);
        if (latestSearchId.current !== searchId) return;

        handleSearchSuccess(response);
      } catch (error) {
        if (latestSearchId.current !== searchId) return;

        throw error;
      }
    };
    await withApiHandler(apiFunction, [username], {}, false);
  };

  const clearSearchList = () => {
    latestSearchId.current += 1;
    clearSearchListStore();
  };

  const getArchiveIndex = async () => {
    const apiFunction = async () => {
      handleArchiveIndexSuccess(await getPublicArchiveIndex());
    };
    await withApiHandler(apiFunction, ['archive-index'], {}, false);
  };

  return {
    searchList,
    search,
    clearSearchList,
    getArchiveIndex,
  };
};
